/**
 * @author Hadeer Elbanna
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2019-07-25 03:10
 * @description Implementation of the Video Panel.
 * @filename  video-panel.tsx
 */
import $ from "jquery";
import React from "react";
import videojs from "video.js";
import YouTube, { Options } from "react-youtube";
import { MediaAssetFile } from "../../utilities/media-asset-file";
import { RdvButton } from "./rdv-button";
import { SVG } from "../../component-configuration/svgs";
import { CommentArea } from "./comment-area";
import { facebookPixelTraceEvent, gaEvent, videoMilestone } from "../../utilities/general";
import isEmpty from "lodash.isempty";
import { YoutubeUtilities } from "../../utilities/youtube-utilities";

const opts: Options = {
  height: "100%",
  width: "100%",
  playerVars: {
    autoplay: 1,
    controls: 1,
    modestbranding: 1,
    rel: 0,
  },
};

const volumeControlImageUrl: string = MediaAssetFile.getImageUrlFromMediaAssetFile(
  {
    name: "EhPfaCFCSzeocmCZeUblvQ.svg",
    path: "eh/pf",
  },
  null,
  false,
);

const volumeControlMutedImageUrl: string = MediaAssetFile.getImageUrlFromMediaAssetFile(
  {
    name: "RS58CELhRaCTMKbQQ9oYwg.svg",
    path: "rs/58",
  },
  null,
  false,
);

/**
 * The props of the Video Panel component.
 *
 * @interface
 */
interface VideoPanelProps {
  /**
   * generic props to use as stand alone component
   *
   * @type {{id:string}}
   * @memberof  VideoPanelProps
   */
  json?: {
    /**
     * event id
     *
     * @type {string}
     */
    event?: string;

    /**
     * external youtube video id
     *
     * @type {string}
     */
    youtube?: string;

    /**
     * event status
     *
     * @type {EventStatusKind}
     */
    status?: any;
  };

  /**
   * to prevent the video from autoplay
   *
   * @type {boolean}
   * @memberof VideoPanelProps
   */
  disableAutoPlay?: boolean;

  /**
   * the video position on screen
   *
   * @type {"FIT" | "FULL_SCREEN" | "FLOAT"}
   * @memberof VideoPanelProps
   */
  videoPosition?: "FIT" | "FULL_SCREEN" | "FLOAT";

  /**
   * if panel should have back button
   *
   * @type {boolean}
   * @memberof VideoPanelProps
   */
  hasBackBtn?: boolean;

  /**
   * true if the video is a youtube this represents the id of youtube video
   *
   * @type {string}
   * @memberof  VideoPanelProps
   */
  youTube?: string;

  /**
   * true if the video is uploaded to our s3 space this represents the id of mp4/hls/excerpt
   *
   * @type {string}
   * @memberof  VideoPanelProps
   */
  videoSrc?: string;

  /**
   * when the video has ended callback
   *
   * @type {() => void}
   * @memberof  VideoPanelProps
   */
  onVideoEnded?: () => void;

  /**
   * to display/hide the play button
   *
   * @type {boolean}
   * @memberof VideoPanelProps
   */
  displayButton?: boolean;

  /**
   * parenet handled mute state
   *
   * @type {boolean}
   * @memberof VideoPanelProps
   */
  mute?: boolean;

  /**
   * invoke mute state change on parent controller
   *
   * @type {() => void}
   * @memberof VideoPanelProps
   */
  onMuteChange?: () => void;

  /**
   *
   *
   * @type {boolean}
   * @memberof VideoPanelProps
   */
  showControls?: boolean;
  /**
   *
   *
   * @type {boolean}
   * @memberof VideoPanelProps
   */
  loop?: boolean;

  onReady?: () => void;
  onPlay?: () => void;

  sendAnalyticEvent?: boolean;
  disableSeek?: boolean;
}

/**
 *
 *
 * @interface VideoPanelState
 */
interface VideoPanelState {
  /**
   * The video mediassets graph result.
   *
   * @type {any}
   */
  graphResult?: any;

  /**
   * to display/hide the chatbox
   *
   * @type {boolean}
   * @memberof VideoPanelState
   */
  liveChatVisible: boolean;

  /**
   * state of the video is playing or paused
   *
   * @type {boolean}
   * @memberof VideoPanelState
   */
  isPlaying: boolean;

  /**
   * A flag which is true if the video is muted.
   *
   * @type {boolean}
   */
  muted: boolean;

  /**
   * The svg of buttons.
   *
   * @type {any}
   */
  rdvSVG: any;

  /**
   * A flag which is true if the video is muted.
   *
   * @type {boolean}
   */
  showVolumControl: boolean;

  /**
   *
   *
   * @type {number}
   * @memberof VideoPanelState
   */
  seekTime?: number;

  YTPlayer?: any;
}
/**
 *  A React component that renders the VideoPanel.
 *
 * @export
 * @class  Video
 * @extends {React.Component< VideoProps>}
 */
export class VideoPanel extends React.Component<VideoPanelProps, VideoPanelState> {
  videoRef: React.RefObject<HTMLDivElement>;
  youtubePlayerRef: React.RefObject<YouTube>;
  player: ReturnType<typeof videojs>;
  private highestVideoMilestoneSent = -1;
  interval: NodeJS.Timeout;
  /**
   * save the value of window scrollTop
   *
   * @type {number}
   */
  windowScrollValue: number;

  /**
   * Constructor.
   *
   * @param { VideoProps} props The props of the component.
   */
  constructor(props: VideoPanelProps) {
    super(props);
    this.windowScrollValue = 0;

    this.videoRef = React.createRef();
    this.youtubePlayerRef = React.createRef();

    this.state = {
      liveChatVisible: this.props.json && this.props.json.status === "live" ? true : false,
      muted: this.props.hasOwnProperty("mute") ? this.props.mute : true,
      isPlaying: !this.props.disableAutoPlay,
      rdvSVG: SVG.radivisionPlay,
      YTPlayer: null,

      showVolumControl:
        (this.props.youTube && this.props.videoPosition === "FLOAT") ||
        (this.props.json && this.props.videoPosition === "FIT")
          ? false
          : true,
    };
    this.playPauseVideo = this.playPauseVideo.bind(this);
    this.toggleSound = this.toggleSound.bind(this);
    this.onWindowScroll = this.onWindowScroll.bind(this);
    this.addingAnimation = this.addingAnimation.bind(this);
    this.handleVideoJsPlayer = this.handleVideoJsPlayer.bind(this);
    this.sendGAEvent = this.sendGAEvent.bind(this);
    this.pauseVideo = this.pauseVideo.bind(this);
    this.playVideo = this.playVideo.bind(this);
    this.getYoutubeCurrentTime = this.getYoutubeCurrentTime.bind(this);
  }

  getYoutubeCurrentTime() {
    const currentTime = (this.youtubePlayerRef?.current as any)?.internalPlayer?.getCurrentTime();
    return currentTime;
  }

  pauseVideo() {
    (this.youtubePlayerRef?.current as any)?.internalPlayer?.pauseVideo();
  }

  playVideo() {
    (this.youtubePlayerRef?.current as any)?.internalPlayer?.playVideo();
  }

  /**
   *
   *
   * @param {string} videoId
   * @param {number} time
   * @memberof VideoPanel
   */
  setElapsed(videoId: string, time: number): void {
    YoutubeUtilities.saveVideoMetaData(videoId, time);
  }

  componentDidMount() {
    // when event is live >> check for new comments on interval set of time
    if (this.props.json && (this.props.json.status === "live" || this.props.json.event)) {
      document.body.classList.add("force-untransparent");
    }

    this.handleVideoJsPlayer();
    // window.XddEventListener("scroll", this.onWindowScroll);
    // if video is full screen > disable body overflow scroll on background
    if (this.props.videoPosition === "FULL_SCREEN") {
      this.windowScrollValue = document.documentElement.scrollTop;
      document.body.classList.remove("allow-scroll");
      document.body.style.marginTop = `-${this.windowScrollValue}px`;
    }
  }

  componentDidUpdate(prevProps: VideoPanelProps) {
    if (
      (prevProps.videoSrc !== this.props.videoSrc && this.props.videoSrc) ||
      prevProps.youTube !== this.props.youTube ||
      prevProps.json?.youtube !== this.props?.json?.youtube
    ) {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = undefined;
      }
      this.highestVideoMilestoneSent = -1;
      this.handleVideoJsPlayer();
    }
    if (prevProps.mute !== this.props.mute) {
      this.setState({ muted: this.props.mute });
      this.toggleSound(this.props.mute);
    }
    // if video is full screen > disable body overflow scroll on background
    if (prevProps.videoPosition !== this.props.videoPosition) {
      if (this.props.videoPosition === "FULL_SCREEN") {
        this.windowScrollValue = document.documentElement.scrollTop;
        document.body.classList.remove("allow-scroll");
        document.body.style.marginTop = `-${this.windowScrollValue}px`;
      } else if (prevProps.videoPosition === "FULL_SCREEN") {
        // overflow was removed by the comonent from previous update >> then reset
        document.body.style.marginTop = "0";
        document.body.classList.add("allow-scroll");
        window.scrollTo(0, this.windowScrollValue);
      }
    }
  }

  /**
   * when current video users video-js player plugin
   */
  handleVideoJsPlayer() {
    if (this.videoRef.current) {
      const videoId = this.props.videoSrc;
      this.player = videojs(this.videoRef.current, {
        errorDisplay: false,
        autoplay: !this.props.disableAutoPlay ? "any" : false,
        muted: this.state.muted,
      });
      this.player.ready(() => {
        if (!this.props.disableAutoPlay) {
          this.player.play();
        }
      });
      this.player.on("timeupdate", () => {
        const elapsed_sec = this.player.currentTime();
        const duration = this.player.duration();
        this.sendGAEvent(elapsed_sec, duration, videoId);
      });
      this.player.on("ended", () => {
        const duration = this.player.duration();
        gaEvent("videoJs", "ended", videoId, true, 100, duration, duration);
      });
    }

    const youtubeId = this.props.json && this.props.json.youtube ? this.props.json.youtube : this.props.youTube;

    if (youtubeId) {
      try {
        const TIME: number = YoutubeUtilities.extractLocalVideoMeta(youtubeId) as number;

        if (TIME) {
          this.setState({ seekTime: TIME });
        }

        if (!this.interval) {
          this.interval = setInterval(async () => {
            const YOUTUBE_REF = this.youtubePlayerRef?.current as any;

            if (this.state.isPlaying) {
              const elapsed_sec: number = await YOUTUBE_REF?.internalPlayer?.getCurrentTime(); // this is a promise.
              const duration: number = await YOUTUBE_REF?.internalPlayer?.getDuration();
              this.setElapsed(youtubeId, elapsed_sec);
              this.sendGAEvent(elapsed_sec, duration, youtubeId);
            }
          }, 1000);
        }
      } catch (error) {}
    }
  }

  /**
   *
   *
   */
  sendGAEvent(elapsed_sec: number, duration: number, youtubeId: string): void {
    if (!duration) {
      return;
    }
    const milestone = videoMilestone((elapsed_sec / duration) * 100);
    if (milestone !== undefined && milestone > this.highestVideoMilestoneSent) {
      this.highestVideoMilestoneSent = milestone;
      gaEvent("youtubeIframe", "milestone", youtubeId, true, milestone, duration, elapsed_sec);
    }
  }
  /**
   *  handle play and pause button if the user clicked them
   */
  addingAnimation() {
    $(".about-video")
      .removeClass("pbAnimation In")
      .hide();
    setTimeout(() => {
      $(".about-video")
        .addClass("pbAnimation In")
        .show();
    }, 5);
  }

  toggleSound(muted: boolean) {
    this.setState({
      muted,
      showVolumControl: true,
    });
    if (this.state.YTPlayer) {
      muted ? this.state.YTPlayer.mute : this.state.YTPlayer.unmute();
    }
    if (this.videoRef?.current) {
      this.videoRef.current.muted = muted;
    }
  }
  /**
   *  handle play and pause button if the user clicked them
   */
  playPauseVideo() {
    $(".video-logo").hide();
    this.setState({
      showVolumControl: true,
    });
    if (this.props.youTube && this.props.videoPosition !== "FLOAT") {
      this.setState({
        rdvSVG: "",
      });
    } else {
      this.addingAnimation();
      const newPlayingState = !this.state.isPlaying;
      this.setState({
        isPlaying: newPlayingState,
        rdvSVG: newPlayingState ? SVG.pause : SVG.play,
        muted: !this.state.muted,
      });
    }

    facebookPixelTraceEvent(event, {
      category: "Play Video",
      title: `${"Hero Video Played"}`,
    });
  }

  /**
   * A function invoked by the event framework when the window scrolls.
   */
  onWindowScroll(): void {
    //$(".pbAnimation").hide();
    // if (mediaVideo !== null && mediaVideo !== undefined) {
    //   let player = videojs(this.props.videoId);
    //   if (!player) {
    //     player = videojs(mediaVideo, {
    //       errorDisplay: false,
    //     });
    //   }
    //   if (window.scrollY > 320) {
    //     player.pause();
    //   } else {
    //     player.play();
    //   }
    // }
    //
    //this.setState({
    //  rdvSVG: "",
    //  muted: true,
    //});
    // this.props.youTube ? this.setState({ showVolumControl: false }) : undefined;
  }

  /**
   * Invoked by the React framework when the component is unmounted.
   */
  componentWillUnmount(): void {
    // window.removeEventListener("scroll", this.onWindowScroll);
    // when video is full screen reset body overflow
    if (this.props.videoPosition === "FULL_SCREEN") {
      document.body.style.marginTop = "0";
      document.body.classList.add("allow-scroll");
      window.scrollTo(0, this.windowScrollValue);
    }
    const TICKER = document.getElementById("ticker_tap");
    if (TICKER) {
      $(".navbar").addClass("addTopMargin");
    }
    if (this.player) {
      this.player.dispose();
    }

    if (this.interval) {
      clearInterval(this.interval);
      this.interval = undefined;
    }
  }

  onReady = (event) => {
    // access to player in all event handlers via event.target
    this.setState({
      YTPlayer: event.target,
    });
    this.seekTo(this.state.seekTime);
    event.target.playVideo();
    if (typeof this.props.onReady === "function") {
      this.props?.onReady();
    }
  };

  seekTo = (seconds: number) => {
    if (this.props.disableSeek) {
      this.state.YTPlayer?.seekTo(0);
    } else if (seconds && seconds > 0) {
      this.state.YTPlayer?.seekTo(seconds);
    }

    this.state.YTPlayer?.playVideo();
  };

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    const youtubeId = this.props.json && this.props.json.youtube ? this.props.json.youtube : this.props.youTube;

    return (
      <div
        className={`video-panel${
          this.props.videoPosition === "FIT"
            ? " full-width-video"
            : this.props.videoPosition === "FULL_SCREEN"
            ? " full-screen"
            : ""
        }${
          this.props.json && this.props.json.status === "live"
            ? ` live-now ${this.state.liveChatVisible ? "chat-visible" : "chat-hidden"}`
            : this.props.json && this.props.json.event
            ? " event-page"
            : ""
        }`}
      >
        <div className="video-container">
          {!isEmpty(this.props.youTube) ||
          (this.props.json !== null &&
            this.props.json !== undefined &&
            this.props.json.youtube !== null &&
            this.props.json.youtube !== undefined) ? (
            <div className={this.props.youTube ? "youtube-video" : this.props.json ? "eventYT-video" : ""}>
              {this.props.hasBackBtn || this.props.videoPosition === "FULL_SCREEN" ? (
                <a
                  className="back"
                  onClick={() => {
                    if (typeof this.props.onVideoEnded === "function") this.props.onVideoEnded();
                    $(".main-navbar").removeAttr("style");
                  }}
                >
                  {SVG.chevronLeft}
                  <span>Back To Browse</span>
                </a>
              ) : null}
              <div
                className={this.props.videoPosition === "FLOAT" ? "vid-bg" : "video"}
                onClick={() => this.toggleSound()}
              >
                {youtubeId && (
                  <YouTube
                    videoId={youtubeId}
                    opts={
                      this.props.json
                        ? {
                            height: "100%",
                            width: "100%",
                            playerVars: {
                              rel: 0,
                              autoplay: Number(!this.props.disableAutoPlay),
                              mute: Number(this.state.muted),
                              loop: Number(this.props?.loop),
                              playlist: this.props?.loop ? this.props?.json?.youtube : undefined,
                              start: this.state.seekTime,
                              control: this.props.showControls === false ? 0 : 1,
                            },
                          }
                        : opts
                    }
                    id={this.props.json && this.props.json.youtube ? this.props.json.youtube : this.props.youTube}
                    ref={this.youtubePlayerRef}
                    onReady={this.onReady}
                    onPlay={this.props.onPlay || $.noop}
                    onEnd={() => {
                      if (typeof this.props.onVideoEnded === "function") this.props.onVideoEnded();
                    }}
                    onStateChange={(event: any) => {
                      /*
                    YT.PlayerState.ENDED   0
                    YT.PlayerState.PLAYING 1
                    YT.PlayerState.PAUSED  2
                    YT.PlayerState.BUFFERING 3
                    YT.PlayerState.CUED 4
                    */
                      switch (event?.data) {
                        case 0:
                          gaEvent(
                            "youtubeIframe",
                            "complete",
                            youtubeId,
                            true,
                            100,
                            event?.target?.getDuration?.(),
                            event?.target?.getCurrentTime?.(),
                          );
                          YoutubeUtilities.removeMetaItem(youtubeId);
                          break;
                        case 1:
                          this.setState({ isPlaying: true });
                          // possibly redundant as it will always say 0 this.sendGAEvent(0, duration, youtubeId);
                          break;
                        case 2:
                          this.setState({ isPlaying: false });
                          break;

                        default:
                          break;
                      }
                    }}
                    className="iframe"
                    width="100%"
                    height="100%"
                  />
                )}
              </div>
              {false && this.props.youTube && this.props.displayButton ? (
                <RdvButton extraClassName="btn-radivision about-video" onClick={this.toggleSound}>
                  {this.state.rdvSVG}
                </RdvButton>
              ) : null}
            </div>
          ) : null}
          {isEmpty(this.props.youTube) && !isEmpty(this.props.videoSrc) ? (
            <div className="originalVideoDetails">
              {this.props.videoPosition === "FLOAT" && !this.props.displayButton ? (
                <a
                  className="back"
                  onClick={() => {
                    if (typeof this.props.onVideoEnded === "function") this.props.onVideoEnded();
                  }}
                >
                  {SVG.chevronLeft}
                  <span>Back To Browse</span>
                </a>
              ) : null}
              <video
                preload="auto"
                ref={this.videoRef}
                muted={this.state.muted}
                className="video-js"
                controls={this.props.showControls ? this.props.showControls : false}
                autoPlay={!this.props.disableAutoPlay}
                loop={this.props.loop}
                playsInline={!this.props.disableAutoPlay}
                onPlay={() => {
                  this.setState({ showVolumControl: true });
                  if (typeof this.props?.onReady === "function") {
                    this.props?.onReady();
                  }
                  if (typeof this.props?.onPlay === "function") {
                    this.props?.onPlay();
                  }
                }}
                onEnded={() => {
                  if (typeof this.props.onVideoEnded === "function") this.props.onVideoEnded();
                }}
                onClick={() => {
                  this.playPauseVideo();
                }}
              >
                {this.props.videoSrc !== null && this.props.videoSrc?.includes("m3u8") ? (
                  <source src={this.props.videoSrc} type="application/x-mpegURL" />
                ) : (
                  <source src={this.props.videoSrc} type="video/mp4" />
                )}
              </video>
              <img
                loading="lazy"
                alt="volume control"
                className="volume-control"
                src={!this.state.muted ? volumeControlImageUrl : volumeControlMutedImageUrl}
                onClick={this.playPauseVideo}
              />
            </div>
          ) : null}
        </div>

        {this.props.json && this.props.json.status === "live" ? (
          <CommentArea
            visible={this.state.liveChatVisible}
            toggleVisibility={() => this.setState({ liveChatVisible: true })}
            isAuthenticated={false}
            eventId={this.props.json && this.props.json.event ? this.props.json.event : undefined}
            onDismiss={() => this.setState({ liveChatVisible: false })}
            youtube={this.props.json.youtube}
          />
        ) : null}
      </div>
    );
  }
}

export default VideoPanel;
function KEY_LOCAL_STORAGE_YOUTUBE_PLAY_TIME(KEY_LOCAL_STORAGE_YOUTUBE_PLAY_TIME: any) {
  throw new Error("Function not implemented.");
}
